import React, { useCallback, useEffect, useMemo } from "react";
import { isEmpty } from "lodash";
import popupStore from "../../store/popupStore";
import OrderTracker from "./OrderTracker";
import OrderStatusTracker from "./OrderStatusTracker";
import ProductItem from "./ProductItem";
import OrderSummary from "./OrderSummary";
import IMG from "../../constants/image";
import ImageBasic from "../Common/Image/ImageBasic";
import { text } from "../../constants/text";
import appStore from "../../store/appStore";
import { useNavigate, useSearchParams } from "react-router-dom";
import { clsx } from "clsx";
import { copyToClipboard, formatDate } from "../../utils";
import { fetchOrderDetail } from "../../services/orderServices";
import { useOrdersStore } from "../../store/orderStore";
import { STATUS_ORDERS } from "../../constants";
import Loading from "../Common/Loading/Loading";

const OrderDetails = () => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams();
    const id = searchParams.get('id');
    const { orderDetail } = useOrdersStore();
    const { subTotal, totalPayment, orderItems, shippingFee, status, shipping, createdAt } = orderDetail || {}
    const { userAddr, trackingInfo } = shipping || {}
    const { address, commune, district, noted, phoneNumber, province, name } = userAddr || {}
    const addressShip = !isEmpty(userAddr) && `${address}, ${commune}, ${district}, ${province}`
    const { isLoading, setLoading } = appStore()
    const { setPopup } = popupStore()

    const orderStatus = useMemo(() => {
        if (!status) return {};
        const isProcessing = status === 'shipping_processing';
        const isPending = ['seller_confirm', 'system_process', 'seller_processing', 'seller_prepared'].includes(status);
        const isDelivered = status === 'shipping_done' || status === 'order_complete';
        const isCancelled = status === 'order_cancel';
        const isReturned = status === 'order_refund';
        return { isProcessing, isPending, isDelivered, isCancelled, isReturned };
    }, [status]);

    const iconOrder = useMemo(() => {
        if (orderStatus.isPending) return IMG.icon.orderPending
        if (orderStatus.isDelivered) return IMG.icon.orderSuccess;
        if (orderStatus.isProcessing) return IMG.icon.cube;
        if (orderStatus.isCancelled) return IMG.icon.orderCancel;
        if (orderStatus.isReturned) return IMG.icon.orderReturned;
        return '';
    }, [orderStatus]);

    const onHandleContact = (phoneNumber) => {
        if (phoneNumber) {
            if (typeof window !== 'undefined' && window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(`Call: ${phoneNumber}`);
            } else window.location.href = `tel:${phoneNumber}`;
        }
        setPopup({})
    };
    const handleBack = () => {
        navigate(-1);
    }


    useEffect(() => {
        if (id) {
            fetchOrderDetail(id).then(res => {
                if (res) setLoading(false)
            })
        }
    }, [id]);

    const getStatusName = useCallback((statusValue) => {
        const statusObj = STATUS_ORDERS.find(item => item.value === statusValue);
        return statusObj ? statusObj.name : statusValue;
    }, []);
    if (isLoading) return <Loading />;
    if (isEmpty(orderDetail)) return null;
    return (
        <div className="animate-fadeIn max-w-4xl mx-auto text-sm">
            <div className='relative hidden md:flex gap-[5px] justify-center items-center mb-5'>
                <div className='absolute top-0 left-0 flex items-center space-x-2 cursor-pointer' onClick={handleBack}>
                    <ImageBasic src={IMG.icon.chevronLeftBlack} alt='chevron left black' />
                    <div className="font-medium">{text.back}</div>
                </div>
                <ImageBasic className='w-6 h-6' src={IMG.icon.clipboardList} alt="clipboard" />
                <p className='text-sm md:text-xl font-bold'>{text.detailOrders}</p>
            </div>
            <OrderTracker orderDetail={orderDetail} />
            <OrderStatusTracker orderDetail={orderDetail} />
            <div className="md:hidden bg-[#FFFCF3] p-4 border-b border-dashed border-gray-200 ">
                <div className="flex justify-between items-start space-y-2">
                    <div className='space-y-2 w-full'>
                        <div className="flex justify-between  border-b pb-2 items-center space-x-2">
                            <div
                                className='flex items-center justify-center space-x-1'>
                                <ImageBasic className='w-5' src={IMG.icon.cube}
                                    alt="cube" />
                                <div>{`Mã đơn hàng: ${id}`}</div>
                            </div>
                            <div
                                onClick={() => copyToClipboard(id)}
                                className='flex items-center justify-center space-x-1'>
                                <div className=''>{text.copy}</div>
                                <ImageBasic className='w-5' src={IMG.icon.copy}
                                    alt="copy" />
                            </div>
                        </div>
                        <div
                            className='flex items-center border-b pb-2 space-x-1'>
                            <ImageBasic className='w-5' src={IMG.icon.calendar}
                                alt="calendar" />
                            <div>{`Ngày đặt: ${formatDate(createdAt)}`}</div>
                        </div>
                        <div className="text-sm space-y-1">
                            <div className='flex justify-between'>
                                <div className='flex items-start justify-start space-x-1'>
                                    <ImageBasic className='w-5' src={IMG.icon.location}
                                        alt="adress" />
                                    <div>
                                        <div className='font-bold'>{name}
                                            <span className='font-normal'>{` - ${phoneNumber}`}</span></div>
                                        <div className="text-gray-500 text-xs mt-1 max-w-[250px]">
                                            {addressShip}
                                        </div>
                                    </div>
                                </div>
                                <div
                                    onClick={() => onHandleContact(phoneNumber)}
                                    className='flex items-start justify-center space-x-1'>
                                    <div className=''>{text.contact}</div>
                                    <ImageBasic className='w-5' src={IMG.icon.phone}
                                        alt="phone" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="bg-white rounded-b-xl">
                <div className="hidden md:flex justify-between items-center bg-[#FFFCF3] p-4 border-b border-gray-200">
                    <div className='flex items-center space-x-4'>
                        <div className='flex items-center cursor-pointer'>
                            <ImageBasic className='w-5 h-5' src={IMG.icon.cube} />
                            <div className="max-w-[300px] line-clamp-2 pl-2">{`Mã đơn hàng: ${id}`}</div>
                        </div>
                        <div className='flex items-center cursor-pointer'>
                            <ImageBasic className='w-5 h-5' src={IMG.icon.calendar} />
                            <div
                                className="max-w-[220px] line-clamp-2 pl-2">{`Ngày đặt: ${formatDate(createdAt)}`}</div>
                        </div>
                    </div>
                    <div
                        className={clsx('flex items-center text-xs space-x-2  px-2 md:px-4 py-1 rounded-2xl', orderStatus?.isCancelled ? 'bg-gray-200' : 'bg-[#E8F2EE]')}>
                        <ImageBasic className='w-[18px] h-[18px]' src={iconOrder} alt='iconOrder' />
                        <div className="">{getStatusName(status)}</div>
                    </div>

                </div>
                {orderItems?.map((product, index) => (
                    <ProductItem key={index} product={product} />
                ))}
                <OrderSummary
                    data={orderDetail}
                    orderGroupId={orderDetail?.id}
                    addressShip={addressShip}
                    orderStatus={orderStatus}
                    isDetail={true}
                />
            </div>
        </div>
    );
};

export default OrderDetails;